import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { api, apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Card, Btn, Badge } from "../../components/UI";
import { FLabel, FInput, FSelect, FTextarea, YesNo } from "../engagement/FormControls";
import { SignModal } from "../../components/SignatureCanvas";
import { SignatureDisplay } from "./shared";
import { ArrowLeft, Lock, PenLine } from "lucide-react";
import { toast } from "sonner";
import ClinicalRecordLockControls from "../../components/ClinicalRecordLockControls";
import { useSaveShortcut, SAVE_HINT } from "../../lib/useSaveShortcut";

function Question({ q, value, onChange, disabled }) {
  if (q.type === "textarea") return <FTextarea data-testid={`aq-${q.key}`} rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} disabled={disabled} />;
  if (q.type === "select") return (
    <FSelect data-testid={`aq-${q.key}`} value={value || ""} onChange={(e) => onChange(e.target.value)} disabled={disabled}>
      {q.options.map((o) => <option key={o}>{o}</option>)}
    </FSelect>
  );
  if (q.type === "yesno") return <YesNo value={value} onChange={onChange} testId={`aq-${q.key}`} />;
  if (q.type === "scale") return (
    <div className="flex flex-wrap gap-1.5">
      {Array.from({ length: 11 }, (_, i) => String(i)).map((n) => (
        <button key={n} type="button" data-testid={`aq-${q.key}-${n}`} disabled={disabled} onClick={() => onChange(n)}
          className={`h-9 w-9 rounded-full text-sm font-bold transition-colors ${value === n ? "bg-[#4E342E] text-white" : "bg-[#F5F2EB] text-[#7B6157] hover:bg-[#EDE7DC]"}`}>{n}</button>
      ))}
    </div>
  );
  return <FInput data-testid={`aq-${q.key}`} value={value || ""} onChange={(e) => onChange(e.target.value)} disabled={disabled} />;
}

export default function AssessmentFill() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = location.state?.from || { url: "/clinical/assessments", label: "Assessments" };
  const goBack = () => navigate(returnTo.url);
  const { user } = useAuth();
  const [assessment, setAssessment] = useState(null);
  const [responses, setResponses] = useState({});
  const [busy, setBusy] = useState(false);
  const [signOpen, setSignOpen] = useState(false);

  const load = () => {
    api.get(`/assessments/${id}`).then((r) => { setAssessment(r.data); setResponses(r.data.responses || {}); })
      .catch(() => { toast.error("Assessment not found"); navigate("/clinical/assessments"); });
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  useSaveShortcut(() => { if (!busy) save(); }, !!assessment && !(assessment.status === "signed" || assessment.locked));

  if (!assessment) return <div className="py-20 text-center text-[#84675B]">Loading assessment…</div>;
  const locked = assessment.status === "signed" || assessment.locked;

  const save = async (status) => {
    setBusy(true);
    try {
      const r = await api.put(`/assessments/${id}`, { responses, status: status || assessment.status });
      setAssessment(r.data);
      toast.success(status === "completed" ? "Assessment completed" : "Responses saved");
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  const sign = async ({ name, image }) => {
    setBusy(true);
    try {
      await api.put(`/assessments/${id}`, { responses });
      const r = await api.put(`/assessments/${id}/sign`, { name, image });
      setAssessment(r.data); setSignOpen(false);
      toast.success("Assessment signed & locked");
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  return (
    <div data-testid="assessment-fill">
      <button data-testid="assess-back-btn" onClick={goBack} className="mb-3 flex items-center gap-1 text-sm text-[#7B6157] hover:underline">
        <ArrowLeft size={15} /> Back to {returnTo.label}
      </button>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-xl font-bold text-[#3E2723]">{assessment.template_name}</h1>
          <div className="text-sm text-[#7B6157] mt-0.5">{assessment.client_name} · <Badge value={assessment.status} /></div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {locked && !assessment.locked && <span data-testid="assess-locked-banner" className="inline-flex items-center gap-1.5 rounded-full bg-[#E8F5E9] px-3.5 py-1.5 text-xs font-bold text-[#2E7D32]"><Lock size={12} /> Signed</span>}
          <ClinicalRecordLockControls kind="assessment" record={assessment} onChange={load}
            amendableFields={[{ key: "responses", label: "Responses (JSON)", type: "textarea", rows: 6 }]} />
        </div>
      </div>

      <div className="space-y-5">
        {assessment.sections.map((s, si) => (
          <Card key={si} className="p-6" data-testid={`assess-section-${si}`}>
            <h3 className="font-heading text-sm font-bold text-[#3E2723] uppercase tracking-wide border-b border-[#F1EEE7] pb-2 mb-4">{s.title}</h3>
            <div className="space-y-4">
              {s.questions.map((q) => (
                <div key={q.key}>
                  <FLabel>{q.label}</FLabel>
                  <Question q={q} value={responses[q.key]} disabled={locked}
                    onChange={(v) => setResponses((r) => ({ ...r, [q.key]: v }))} />
                </div>
              ))}
            </div>
          </Card>
        ))}

        {assessment.signature && <SignatureDisplay sig={assessment.signature} label="Clinician Signature" />}

        {!locked && (
          <div className="flex flex-wrap justify-end gap-3">
            <Btn data-testid="assess-save-btn" variant="outline" onClick={() => save()} disabled={busy} title={`Save (${SAVE_HINT})`} aria-keyshortcuts="Control+S">Save Responses</Btn>
            <Btn data-testid="assess-complete-btn" variant="outline" onClick={() => save("completed")} disabled={busy}>Mark Completed</Btn>
            <Btn data-testid="assess-sign-btn" onClick={() => setSignOpen(true)} disabled={busy}><PenLine size={14} /> Sign & Lock</Btn>
          </div>
        )}
      </div>

      <SignModal open={signOpen} onClose={() => setSignOpen(false)} onSign={sign} busy={busy}
        title="Sign Assessment" defaultName={`${user?.first_name || ""} ${user?.last_name || ""}`.trim()} />
    </div>
  );
}
